/**
 * PurgeReasonCodesPage — System · Purge Reason Codes.
 *
 * Closed catalog of typed reasons the SuperAdmin Records-Management
 * hard-delete / purge dialog reads. Each row is a reason an officer can pick
 * when permanently removing a record (wrong-customer, duplicate-from-stuck-
 * flow, metric-skew-correction, pii-misentry-correction, …). The chosen code
 * is stamped onto every dbo.PurgeHistory row, so codes are audit-permanent.
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/purgeReasonCodesEditor.tsx — the
 * operational app writes no config). The verified wire contract:
 *
 *   - Entity:  PCBJxChangeApi/Data/Entities/Config/Codes/PurgeReasonCode.cs
 *              Columns: Code (PK,32) · DisplayName (128) · Description (1024)
 *              · IsActive · SortOrder · Created/UpdatedAt/By. There is NO
 *              HelperText column — see the drift note below.
 *   - Read:    GET /v3/config/purge-reason-codes
 *              → V3ConfigFunction.PurgeReasonCodes. Returns ALL rows (NO
 *              WHERE IsActive filter), ordered by SortOrder, Code. Typed in
 *              shared-types as PurgeReasonCode and exposed by the api-client
 *              as client.retail.config.purgeReasonCodes(); the declared type
 *              matches the wire, so the typed read is used directly.
 *   - Write:  POST /v3/admin/purge-reason-codes (+ DELETE /{code}).
 *
 * ── DRIFT / NO-OP findings (handled in this page) ────────────────────────
 *  (1) WIRE DUPLICATE: the read emits BOTH `description` AND `helperText`,
 *      and the function sets `helperText = r.Description` — i.e. they are the
 *      SAME single Description column. The ROP-teams editor exposed two
 *      separate inputs ("Helper text" + "Description") and POSTed both, which
 *      can only round-trip ONE of them (silent drop of the other). This page
 *      surfaces a SINGLE Description field (it is the helper text shown under
 *      the radio) so the officer can't enter two values that collapse to one.
 *  (2) MISSING BACKEND WRITE ROUTE: there is currently NO server handler for
 *      POST/DELETE /v3/admin/purge-reason-codes (only the GET config read
 *      exists). The ROP-teams editor's writes therefore 404 silently. This
 *      page posts to the same intended route with the CORRECT admin-panel
 *      auth posture (ADMIN_AUTH_TOKEN_SENTINEL, no bypass-key prompt) so it
 *      goes live the moment the orchestrator/backend wires the handler. Save
 *      surfaces the server error rather than masking it.
 *
 * DELETE choice: SOFT-delete via re-upsert isActive=false. The read does NOT
 * filter on IsActive (inactive rows still load here), and dbo.PurgeHistory
 * rows reference the Code permanently — a hard delete would orphan historical
 * audit rows. isActive stays editable so a code can be reactivated anytime.
 *
 * Auth: MSAL Path C via ADMIN_AUTH_TOKEN_SENTINEL (no bypass-key prompt;
 * bypass-key gated OFF in prod). Mirrors SlaTargetsPage / ParmValSyncPage.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import type { PurgeReasonCode, PurgeReasonCodeUpsertInput } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface ReasonRow {
  code: string;
  label: string;
  /** Single Description column — surfaced as the under-radio helper text. */
  description: string;
  isActive: boolean;
  sortOrder: number;
}

interface EditDraft extends ReasonRow {
  isNew: boolean;
}

const CODE_PATTERN = /^[A-Z0-9_]{1,32}$/;

function emptyDraft(): EditDraft {
  return {
    code: '',
    label: '',
    description: '',
    isActive: true,
    sortOrder: 100,
    isNew: true,
  };
}

function rowToDraft(r: ReasonRow): EditDraft {
  return { ...r, isNew: false };
}

/**
 * Build the upsert body. Only ONE textual sub-field exists server-side
 * (Description); `helperText` is the read-time alias for it, so we send the
 * same value under both keys to satisfy the DTO without losing data.
 */
function draftToUpsertBody(d: EditDraft): PurgeReasonCodeUpsertInput {
  const description = d.description.trim() || undefined;
  return {
    code: d.code.trim(),
    label: d.label.trim(),
    description,
    helperText: description,
    isActive: d.isActive,
    sortOrder: d.sortOrder,
  };
}

export function PurgeReasonCodesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<ReasonRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReasonRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.purgeReasonCodes();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: ReasonRow[] = resp.data.map((r: PurgeReasonCode) => ({
            code: r.code,
            label: r.label,
            // `description` and `helperText` are the same Description column;
            // prefer description, fall back to the helperText alias.
            description: r.description ?? r.helperText ?? '',
            isActive: r.isActive,
            sortOrder: r.sortOrder ?? 100,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load purge reason codes.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const sorted = (rows ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (r) =>
        r.code.toLowerCase().includes(needle) ||
        r.label.toLowerCase().includes(needle) ||
        r.description.toLowerCase().includes(needle),
    );
  }, [rows, filter]);

  const upsert = useCallback(
    async (d: EditDraft): Promise<void> => {
      const resp = await client.http.post<{ success: boolean; error?: string }>(
        '/v3/admin/purge-reason-codes',
        {
          headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
          body: draftToUpsertBody(d),
        },
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const code = draft.code.trim();
    if (!code || !CODE_PATTERN.test(code)) {
      setSaveError('Code must be 1–32 chars (uppercase letters, digits, underscore).');
      return;
    }
    if (!draft.label.trim()) {
      setSaveError('Plain-language label is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created purge reason code "${code}".`
          : `Updated purge reason code "${code}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-disable via re-upsert: dbo.PurgeHistory rows reference Code
      // permanently, so a hard delete would orphan historical audit rows.
      await upsert({ ...deleteTarget, isActive: false, isNew: false });
      setToast({
        tone: 'warn',
        text: `${deleteTarget.code} (${deleteTarget.label}) is now inactive. The hard-delete dialog won't offer it.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<ReasonRow>> = useMemo(
    () => [
      {
        header: 'Code',
        cell: (r) => <code className="cell-mono--bold">{r.code}</code>,
        sortKey: (r) => r.code,
      },
      {
        header: 'Label',
        cell: (r) => <span className="cell-strong">{r.label}</span>,
        sortKey: (r) => r.label,
      },
      {
        header: 'Helper text',
        cell: (r) => <span className="cell-desc">{r.description || '—'}</span>,
      },
      {
        header: 'Order',
        align: 'right',
        cell: (r) => <span className="num">{r.sortOrder}</span>,
        sortKey: (r) => r.sortOrder,
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
        sortKey: (r) => (r.isActive ? 0 : 1),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load purge reason codes: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading purge reason codes…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        These reason codes drive the SuperAdmin Records-Management hard-delete dialog. The reason an
        officer picks is stamped onto the permanent dbo.PurgeHistory audit row, so codes can be
        deactivated but are never hard-deleted here.
      </Banner>

      <Card
        title={`Purge Reason Codes — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="code, label, or helper text…"
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Reason
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          The hard-delete dialog reads from this list. Lower sort order renders first in the reason
          picker. Inactive rows stay for historical audit but no longer appear as a choice.
        </p>
        <DataTable<ReasonRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.code}
          emptyState={<p className="empty">No reason codes match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a purge reason code' : `Edit ${draft?.code ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Code"
                value={draft.code}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({
                    ...draft,
                    code: v.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 32),
                  })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. WRONG_CUSTOMER, DUPLICATE"
                hint={draft.isNew ? 'Up to 32 chars (uppercase, digits, underscore). Permanent.' : 'Permanent.'}
              />
              <NumberField
                label="Sort order"
                value={draft.sortOrder}
                onChange={(v) => setDraft({ ...draft, sortOrder: v == null ? 0 : Math.max(0, Math.min(9999, v)) })}
                min={0}
                max={9999}
                hint="Lower numbers render first in the reason picker."
              />
            </div>

            <TextField
              label="Label"
              value={draft.label}
              onChange={(v) => setDraft({ ...draft, label: v.slice(0, 128) })}
              placeholder="e.g. Wrong customer record"
              hint="Plain-language label shown in the hard-delete reason dropdown."
            />

            <TextArea
              label="Helper text"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v.slice(0, 1024) })}
              rows={3}
              placeholder="e.g. Record was opened against the wrong customer and must be removed."
              hint="Sub-text shown beneath the reason in the dialog explaining when to pick it. Stored in the single Description column."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive reasons hide from the hard-delete dialog but stay for historical audit."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this reason inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark <code className="cell-mono">{deleteTarget.code}</code> (
              {deleteTarget.label}) inactive. The hard-delete dialog won&apos;t offer it as a choice
              anymore, but historical dbo.PurgeHistory rows that reference it stay intact. Flip it
              back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default PurgeReasonCodesPage;
